import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { SIGN_UP, USER_HOME } from '@app/shared/constant/routes';
import { UserTypes } from '@app/shared/enums/user.type';
import { determineCreatedByValue } from '@app/shared/helper/determine-created-by';
import { IUserDeatils } from '@app/shared/models';
import { encryptPassword } from '@app/shared/services/utils';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Constants } from 'src/app/shared/constant/constants';
import { LoaderService, RequestService, SessionManagerService, ToastService } from 'src/app/shared/services/index';
import { environment } from '../../../../../environments/environment';
import { SubscriptionExpirePopupComponent } from '../login/subscription-expire-popup/subscription-expire-popup.component';
import { AccessCodeModalComponent } from '../sign-up/access-code-modal/access-code-modal.component';
import { TermsConditionPopupComponent } from '../terms-condition-popup/terms-condition-popup.component';
import { FirebaseAuthService } from './firebase-auth.service';
import { UserAuthService } from './user-auth.service';
import { TranslateService } from '@ngx-translate/core';
import { THEMES, THEME_IDS } from '@app/shared/services/theme/themes';
import { ThemeService } from '@app/shared/services/theme/theme.service';
import { IUserPreferences } from '@app/shared/models/IUserDetails';

interface UserProfileInfo {
  email: string;
  preferred_username: string;
  sub: string;
}

const authCodeFlowConfig: AuthConfig = {
  issuer: environment.nycdoe.issuer,
  responseType: 'code',
  dummyClientSecret: environment.nycdoe.client_secret,
  strictDiscoveryDocumentValidation: false,
  redirectUri: environment.nycdoe.redirect_uri,
  clientId: environment.nycdoe.client_id,
  scope: 'openid profile email',
  oidc: true,
  showDebugInformation: true,
  requireHttps: true,
};

@Injectable({
  providedIn: 'root',
})
export class NycDoeService {
  socialData: { [key: string]: any } = {};
  public modalRef!: BsModalRef;
  private baseUrl: string;

  constructor(
    private readonly oAuthService: OAuthService,
    private readonly modalService: BsModalService,
    private sessionManagerService: SessionManagerService,
    private userAuthService: UserAuthService,
    private requestService: RequestService,
    private loader: LoaderService,
    private flashMessage: ToastService,
    private router: Router,
    private httpClient: HttpClient,
    private firebaseAuthService: FirebaseAuthService,
    private translateService: TranslateService,
    private themeService: ThemeService,
  ) {
    this.baseUrl = environment.baseApiURL;
    this.oAuthService.configure(authCodeFlowConfig);
  }

  async initiateOAuthProcess() {
    try {
      await this.oAuthService.loadDiscoveryDocument();
      await this.oAuthService.tryLoginCodeFlow();

      this.oAuthService.initLoginFlow();
    } catch (err) {
      console.error('ERROR', JSON.stringify(err));
    }
  }

  async isTokenValid() {
    try {
      this.loader.start();
      await this.oAuthService.loadDiscoveryDocument();
      await this.oAuthService.tryLoginCodeFlow();

      if (this.oAuthService.hasValidAccessToken()) {
        const idToken = this.oAuthService.getIdToken();
        const userProfile: any = await this.oAuthService.loadUserProfile();
        const { email, preferred_username, sub } = userProfile.info as UserProfileInfo;

        this.setUserData({
          email,
          preferred_username,
          sub: `${sub} ${idToken}`,
        });
      } else {
        console.error('No valid access token found');
        this.loader.stop();
        this.router.navigate([SIGN_UP.URL]);
      }
    } catch (err: any) {
      this.loader.stop();
      console.error('Error:', err.message);
      this.translateService.get('ERROR.somethingWentWrong').subscribe((translatedMessage: string) => {
        this.flashMessage.showError(translatedMessage);
      });
    }
  }

  setUserData(user: UserProfileInfo) {
    const priceId = this.sessionManagerService.getPriceId();
    const nycState = JSON.parse(localStorage.getItem('nycState') || '{}');

    this.socialData = {
      email: user.email,
      social: {
        accessKey: user.sub,
        type: Constants.SOCIAL_LOGIN_TYPES.NYCDOE,
      },
    };

    localStorage.removeItem('nycState');

    if (nycState.type === 'Studentlogin') {
      this.socialData['priceId'] = priceId || 'free_trial';
      return this.userSocialLogin(this.socialData);
    } else {
      const { userType, selectedAccountType, parentEmail } = nycState;

      if (userType) {
        this.socialData = {
          ...this.socialData,
          parentEmail: parentEmail || null,
          isTermsAndConditionChecked: true,
          isUserUnderAge: false,
          role: Constants.ROLE_IDS.USER,
          userType: userType === UserTypes.CHILD ? '1' : userType,
          createdBy: determineCreatedByValue(userType, selectedAccountType),
        };

        return this.socialSignupAndShowAccessModal(this.socialData);
      } else {
        this.loader.stop();
        this.translateService.get('ERROR.somethingWentWrong').subscribe((translatedMessage: string) => {
          this.flashMessage.showError(translatedMessage);
        });
        this.router.navigate([SIGN_UP.URL]);
        return;
      }
    }
  }

  socialSignupAndShowAccessModal(socialData: object) {
    return this.requestService.post<{ data: IUserDeatils }>(Constants.ENDPOINTS.socialSignup, socialData).subscribe({
      next: (res: any) => {
        this.loader.stop();
        const { activeToken } = res.data || {};

        if (activeToken) {
          this.modalService.show(AccessCodeModalComponent, {
            ignoreBackdropClick: true,
            initialState: { accessToken: activeToken },
          });
        }

        this.router.navigate([SIGN_UP.URL]);
      },
      error: error => {
        this.loader.stop();
        this.flashMessage.showError(error.error?.errorMessage || 'An error occurred');
        this.router.navigate([SIGN_UP.URL]);
      },
    });
  }

  userSocialLogin(socialData: object) {
    this.loader.start();
    return this.requestService.post<{ data: IUserDeatils }>(Constants.ENDPOINTS.socialLogin, socialData).subscribe({
      next: async (res: any) => {
        const { data } = res || {};

        if (data && data.activeToken) {
          localStorage.setItem('preferences', JSON.stringify(data?.userPreferences));
          const isSubscriptionMandatory = data.isSubscriptionMandatory == false ? '0' : '1';
          await this.updateThemeToContrastTheme(res);

          this.sessionManagerService.createSession(data.activeToken, data.role, data.id, data.levelId, data.levelNo, data.email);
          this.sessionManagerService.setLoggedInData(data);
          this.sessionManagerService.setSubscriptionInfo(data?.subscriptionInfo);
          this.sessionManagerService.setisIntegratedWithGoogle(data?.isGoogleIntegrated);
          this.sessionManagerService.setPlanInfo(data?.planInfo);
          this.sessionManagerService.setIsSubcrpition(isSubscriptionMandatory);
          await this.firebaseAuthService.loginWithToken(data.firebaseToken, res);

          if (!data.isGoogleIntegrated) {
            this.loader.stop();
          }

          if (data.sessionUrl) {
            window.open(data.sessionUrl, '_self');
          } else {
            this.userAuthService.navigationBasedOnUser(data);
          }
        }
      },
      error: error => {
        this.loader.stop();
        const errorMessage = error.error?.errorMessage || 'An error occurred';

        if (error.error?.code === Constants.ERROR_CODES.BAD_REQUEST) {
          const initialState = {
            token: error.error.data?.activeToken,
            type: Constants.SOCIAL_LOGIN_TYPES.MICROSOFT_SIGNIN,
          };
          this.modalService.show(TermsConditionPopupComponent, { initialState });
        } else if (error.error?.status === Constants.ERROR_CODES.CONFLICT) {
          this.flashMessage.showError(errorMessage);
          this.sessionManagerService.setPriceId('free_trial' as string);
          this.router.navigate([USER_HOME.URL]);
        } else if (error.error?.status === Constants.ERROR_CODES.TRANSACTION) {
          const initialState = {
            headerText: errorMessage,
            email: this.socialData.email,
            password: encryptPassword('Hello world'),
            type: true,
          };
          this.modalService.show(SubscriptionExpirePopupComponent, { initialState });
        } else {
          this.flashMessage.showError(errorMessage);
          this.router.navigate([SIGN_UP.URL]);
        }
      },
    });
  }

  nycUserLogout() {
    this.oAuthService.revokeTokenAndLogout();
  }

  async updateThemeToContrastTheme(res: any) {
    const { userPreferences, activeToken }: { userPreferences: IUserPreferences & { _id: string; userRole: number }; activeToken: string } = res.data;
    if (userPreferences && res.data?.userPreferences.theme) {
      if (userPreferences?.isUseContrastColors && (!userPreferences?.theme || userPreferences?.theme !== THEME_IDS.DEFAULT_HIGHCONTRAST)) {
        // * Update the theme to constrast theme.
        if (userPreferences?._id) {
          delete (userPreferences as Partial<typeof userPreferences>)._id;
        }
        try {
          const result: any = await this.httpClient
            .patch(
              `${this.baseUrl}${Constants.ENDPOINTS.userPreferencesSet}`,
              {
                ...userPreferences,
                theme: THEME_IDS.DEFAULT_HIGHCONTRAST,
                isUseContrastColors: false,
                userRole: userPreferences.userRole ? userPreferences.userRole : 1,
              },
              {
                headers: {
                  token: activeToken,
                  'X-Skip-Interceptor': 'true',
                },
              },
            )
            .toPromise();
          localStorage.setItem(
            'preferences',
            JSON.stringify({
              ...userPreferences,
              theme: result.data?.theme,
              isUseContrastColors: result.data?.isUseContrastColors,
            }),
          );

          const theme = THEMES.find(color => color.id === result.data?.theme) || THEMES[0];
          this.themeService.setColor(theme);
        } catch (error) {
          console.error('Error updating theme:', error);
        }
      } else {
        const theme = THEMES.find(color => color.id === res.data?.userPreferences.theme);
        this.themeService.setColor(theme! || THEMES[0]);
      }
    } else {
      this.themeService.setColor(THEMES[0]);
    }
  }
}
